/**
 * "Calculate This Comp" (PLAN §9) — reactive calculator over the imported
 * engine module (one engine, one code path). Every input change recomputes
 * via a 250ms-debounced effect; no calculate button, no network per
 * keystroke. Interactive-only overrides (pricing, fee, adder) ride the
 * engine's `options` argument, which batch runs never pass.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Code,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select as ChakraSelect,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Select, CreatableSelect } from 'chakra-react-select';
import engineModule, { ENGINE_VERSION, FLAG_VOCABULARY, reviveSnapshot, isW2 } from '../engineClient';
import { payrollApi } from '../api';
import {
  emptyCalcState,
  stateToEngineInput,
  stateToRowContext,
  pipelineRowToState,
  monthNameFromDate,
} from './calcState';

const money = (v) => (v === null || v === undefined || v === '' || isNaN(v)
  ? '—'
  : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/** Flag → vocabulary tooltip (keys may carry `:detail` suffixes). */
function flagHelp(fl) {
  if (FLAG_VOCABULARY[fl]) return FLAG_VOCABULARY[fl];
  const base = String(fl).split(':')[0];
  const key = Object.keys(FLAG_VOCABULARY).find((k) => k === base || k.split(':')[0] === base);
  return key ? FLAG_VOCABULARY[key] : 'No vocabulary entry.';
}

function FlagTags({ flags }) {
  if (!flags || !flags.length) return <Text fontSize="sm" color="gray.500">none</Text>;
  return (
    <Flex gap={1} wrap="wrap">
      {flags.map((fl) => (
        <Tooltip key={fl} label={flagHelp(fl)} hasArrow placement="top" maxW="320px">
          <Tag size="sm" colorScheme="orange" cursor="help">{fl}</Tag>
        </Tooltip>
      ))}
    </Flex>
  );
}

function PanelBox({ title, children }) {
  return (
    <Box borderWidth="1px" borderRadius="md" p={3} mb={3}>
      <Heading size="xs" mb={2} textTransform="uppercase" color="gray.500">{title}</Heading>
      {children}
    </Box>
  );
}

function Fact({ label, children }) {
  return (
    <Flex fontSize="sm" gap={2} py={0.5}>
      <Text color="gray.500" minW="130px" flexShrink={0}>{label}</Text>
      <Box>{children}</Box>
    </Flex>
  );
}

const rateFmt = (v, suffix = '%') => (v === null || v === undefined || v === '' ? '—' : `${v}${suffix}`);

/** Poll cadence for pipeline change detection while the page is visible. */
const PIPELINE_POLL_MS = 60000;

export default function CalculatorTab({
  loadRowNum = null,
  onRowLoaded = () => {},
  presetLoEmail = null,
  onPresetConsumed = () => {},
  refreshSignal = 0,
}) {
  const toast = useToast();
  const stepSkipColor = useColorModeValue('gray.400', 'gray.500');
  const overrideBg = useColorModeValue('yellow.50', 'yellow.900');

  const [snap, setSnap] = useState(null); // { revived, hash, loadedAt }
  const [snapError, setSnapError] = useState(null);
  const [liveCounts, setLiveCounts] = useState(null); // { "email|Month": n }
  const [state, setState] = useState(emptyCalcState());
  const [result, setResult] = useState(null); // { ctx, result } | { error }

  const [rowModalOpen, setRowModalOpen] = useState(false);
  const [rowQuery, setRowQuery] = useState('');
  const [rowMatches, setRowMatches] = useState(null);
  const [rowSearching, setRowSearching] = useState(false);
  const [recentRows, setRecentRows] = useState(null); // latest pipeline rows, newest first
  const [recentLoading, setRecentLoading] = useState(false);

  const [goldenOpen, setGoldenOpen] = useState(false);
  const [goldenName, setGoldenName] = useState('');
  const [goldenSaving, setGoldenSaving] = useState(false);

  const set = (patch) => setState((s) => ({ ...s, ...patch }));

  // ---- Snapshot + live month counts (§9.1) ---------------------------------
  const loadSnapshot = async () => {
    setSnapError(null);
    try {
      const { snapshot, hash } = await payrollApi.getReferenceSnapshot();
      setSnap({ revived: reviveSnapshot(snapshot), hash, loadedAt: new Date() });
    } catch (err) {
      setSnapError(err.message || 'Failed to load the reference snapshot.');
    }
  };

  // Pipeline change detection: getMonthCounts returns a content fingerprint;
  // we poll it while the page is visible and auto-refresh when it changes.
  const pipelineFpRef = useRef(null);
  const pollBusyRef = useRef(false);
  const fetchCounts = async ({ notifyOnChange = false } = {}) => {
    if (pollBusyRef.current) return;
    pollBusyRef.current = true;
    try {
      const res = await payrollApi.getMonthCounts();
      const firstLoad = pipelineFpRef.current === null;
      const changed = !firstLoad && res.fingerprint !== pipelineFpRef.current;
      pipelineFpRef.current = res.fingerprint;
      // Keep the counts object stable across no-change polls so downstream
      // effects (month-count default) don't re-fire every minute.
      if (firstLoad || changed) setLiveCounts(res.counts);
      if (changed) {
        loadSnapshot(); // reference data may have changed too — cheap DB read
        if (notifyOnChange) {
          toast({
            title: 'Pipeline sheet changed',
            description: 'Live counts and reference data refreshed automatically.',
            status: 'info',
            duration: 4000,
            isClosable: true,
          });
        }
      }
    } catch {
      if (pipelineFpRef.current === null) setLiveCounts({}); // count input stays manual on failure
    } finally {
      pollBusyRef.current = false;
    }
  };

  useEffect(() => {
    loadSnapshot();
    fetchCounts();
    const tick = () => { if (!document.hidden) fetchCounts({ notifyOnChange: true }); };
    const interval = setInterval(tick, PIPELINE_POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Header "Refresh data" button (and future push triggers) land here.
  useEffect(() => {
    if (!refreshSignal) return;
    loadSnapshot();
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // ---- Backtest → calculator deep link (§9.5) ------------------------------
  useEffect(() => {
    if (!loadRowNum) return;
    let cancelled = false;
    (async () => {
      try {
        const { matches } = await payrollApi.getPipelineRow({ rowNum: loadRowNum });
        if (cancelled) return;
        if (matches.length) {
          setState(pipelineRowToState(matches[0]));
          toast({ title: `Loaded pipeline row ${loadRowNum}`, status: 'info', duration: 3000, isClosable: true });
        } else {
          toast({ title: `Row ${loadRowNum} not found on the live tab`, status: 'warning', duration: 5000, isClosable: true });
        }
      } catch (err) {
        toast({ title: 'Load failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
      } finally {
        if (!cancelled) onRowLoaded();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRowNum]);

  // ---- Reference tab "Try in calculator" (fresh state, LO preselected) -----
  useEffect(() => {
    if (!presetLoEmail) return;
    setState({ ...emptyCalcState(), loEmail: presetLoEmail });
    onPresetConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetLoEmail]);

  // ---- Live month-count default on LO/month change (§9.1) -----------------
  const month = monthNameFromDate(state.closingDate ? new Date(`${state.closingDate}T12:00:00`) : null);
  useEffect(() => {
    if (state.monthCountOverridden || !liveCounts || !state.loEmail || !month) return;
    const live = liveCounts[`${state.loEmail}|${month}`];
    setState((s) => ({ ...s, monthCount: live || 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loEmail, month, liveCounts, state.monthCountOverridden]);

  // ---- Reactive recompute (250ms debounce) ---------------------------------
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!snap) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!state.loEmail) { setResult(null); return; }
      try {
        const { rawRow, options, monthCounts } = stateToEngineInput(state);
        const out = engineModule.evaluateRow(rawRow, snap.revived, monthCounts, options);
        setResult(out);
      } catch (err) {
        setResult({ error: err.message });
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [state, snap]);

  // ---- Dropdown options ----------------------------------------------------
  const loOptions = useMemo(() => {
    if (!snap) return [];
    return Object.values(snap.revived.profiles)
      .sort((a, b) => String(a.name || a.email).localeCompare(String(b.name || b.email)))
      .map((p) => ({
        value: p.email,
        label: `${p.name || p.email} — ${p.email}${p.status && p.status !== 'Active' ? ` (${p.status})` : ''}`,
      }));
  }, [snap]);

  const lenderOptions = useMemo(() => {
    if (!snap) return [];
    const names = new Set(Object.keys(snap.revived.margin || {}));
    Object.keys(snap.revived.marginAliases || {}).forEach((a) => names.add(a));
    return [...names].sort().map((n) => ({ value: n, label: n }));
  }, [snap]);

  const prof = snap && state.loEmail ? snap.revived.profiles[state.loEmail] : null;
  const res = result && !result.error ? result.result : null;
  const resolved = (res && res.resolved) || {};
  const flags = result && !result.error && result.ctx ? result.ctx.flags : [];

  // Lender panel: engine-resolved margin row (aliases + investor handled).
  const marginRow = resolved.marginLender && snap ? snap.revived.margin[resolved.marginLender] : null;

  const expectedRevenue = useMemo(() => {
    if (!res || resolved.marginPct == null || state.loanAmount === '') return null;
    return Number(state.loanAmount) * (resolved.marginPct / 100);
  }, [res, resolved.marginPct, state.loanAmount]);

  const autoFee = res && resolved.fee != null ? resolved.fee : null;
  const hasOverride = state.pricing !== 'auto' || state.feeMode === 'manual' || state.adder !== 'auto';

  // ---- Load-from-row + golden case actions --------------------------------
  const searchRows = async () => {
    if (!rowQuery.trim()) { setRowMatches(null); return; } // empty search → back to the latest-rows list
    setRowSearching(true);
    setRowMatches(null);
    try {
      const { matches } = await payrollApi.getPipelineRow({ query: rowQuery.trim() });
      setRowMatches(matches);
    } catch (err) {
      toast({ title: 'Search failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setRowSearching(false);
    }
  };

  // Latest pipeline rows — fetched fresh each time the modal opens.
  useEffect(() => {
    if (!rowModalOpen) return undefined;
    let cancelled = false;
    setRecentLoading(true);
    payrollApi.getPipelineRow({ recent: 15 })
      .then((res) => { if (!cancelled) setRecentRows(res.matches); })
      .catch(() => { if (!cancelled) setRecentRows([]); })
      .finally(() => { if (!cancelled) setRecentLoading(false); });
    return () => { cancelled = true; };
  }, [rowModalOpen]);

  const loadMatch = (match) => {
    setState(pipelineRowToState(match));
    setRowModalOpen(false);
    setRowMatches(null);
    setRowQuery('');
    toast({ title: `Loaded pipeline row ${match.rawRow.rowNum}`, status: 'info', duration: 3000, isClosable: true });
  };

  const renderRowTable = (rows) => (
    <Table size="sm">
      <Thead>
        <Tr><Th isNumeric>Row</Th><Th>Client</Th><Th>LO</Th><Th>Month</Th><Th isNumeric>Loan</Th><Th>Stamped</Th><Th /></Tr>
      </Thead>
      <Tbody>
        {rows.map((m) => (
          <Tr key={m.rawRow.rowNum}>
            <Td isNumeric>{m.rawRow.rowNum}</Td>
            <Td maxW="160px" isTruncated title={m.rawRow.client}>{m.rawRow.client}</Td>
            <Td maxW="160px" isTruncated title={m.rawRow.primaryLo}>{m.rawRow.primaryLo}</Td>
            <Td>{m.rawRow.month}</Td>
            <Td isNumeric>{money(m.rawRow.loanAmount)}</Td>
            <Td>{m.stamped ? <Badge>paid</Badge> : <Badge colorScheme="teal">open</Badge>}</Td>
            <Td><Button size="xs" colorScheme="blue" onClick={() => loadMatch(m)}>Load</Button></Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );

  const saveGolden = async () => {
    if (!goldenName.trim() || !res) return;
    setGoldenSaving(true);
    try {
      const { id } = await payrollApi.saveGoldenCase({
        name: goldenName.trim(),
        rowContext: stateToRowContext(state),
        expected: {
          primary: res.primary,
          secondary: res.secondary,
          formula: res.formula,
          flags,
          thirdParty: res.thirdParty,
        },
        engineVersion: ENGINE_VERSION,
        source: state.loadedFromRow ? `pipeline row ${state.loadedFromRow}` : 'calculator',
      });
      toast({ title: 'Golden case saved', description: `id ${id} — export with scripts/payroll/export-golden-cases.js`, status: 'success', duration: 6000, isClosable: true });
      setGoldenOpen(false);
      setGoldenName('');
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setGoldenSaving(false);
    }
  };

  const copyFormula = () => {
    if (res && res.formula) {
      navigator.clipboard.writeText(res.formula);
      toast({ title: 'Formula copied', status: 'info', duration: 2000 });
    }
  };

  if (snapError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />{snapError}
        <Button ml={4} size="sm" onClick={loadSnapshot}>Retry</Button>
      </Alert>
    );
  }
  if (!snap) return <Flex justify="center" py={16}><Spinner size="lg" /></Flex>;

  return (
    <Box>
      <Flex gap={2} mb={4} wrap="wrap">
        <Button size="sm" variant="outline" onClick={() => setState(emptyCalcState())}>Reset</Button>
        <Button size="sm" variant="outline" onClick={copyFormula} isDisabled={!res}>Copy formula</Button>
        <Button size="sm" colorScheme="blue" variant="outline" onClick={() => setRowModalOpen(true)}>
          Load from pipeline row
        </Button>
        <Button size="sm" colorScheme="purple" variant="outline" onClick={() => setGoldenOpen(true)} isDisabled={!res}>
          Save as golden test case
        </Button>
      </Flex>

      <Grid templateColumns={{ base: '1fr', lg: '340px 1fr 1fr' }} gap={5} alignItems="start">
        {/* ------------------------- INPUTS (left) ------------------------- */}
        <GridItem>
          <PanelBox title="Inputs">
            <FormControl mb={2}>
              <FormLabel fontSize="sm" mb={1}>Loan officer</FormLabel>
              <Select
                size="sm"
                options={loOptions}
                value={loOptions.find((o) => o.value === state.loEmail) || null}
                onChange={(o) => set({ loEmail: o ? o.value : '', monthCountOverridden: false })}
                placeholder="Search name or email…"
                isClearable
              />
            </FormControl>

            <FormControl mb={2}>
              <FormLabel fontSize="sm" mb={1}>Lender</FormLabel>
              <CreatableSelect
                size="sm"
                options={lenderOptions}
                value={state.lender ? { value: state.lender, label: state.lender } : null}
                onChange={(o) => set({ lender: o ? o.value : '' })}
                placeholder="Margin-tab lender…"
                isClearable
                formatCreateLabel={(input) => `Use "${input}" (not in margin tab)`}
              />
            </FormControl>

            <Checkbox size="sm" isChecked={state.correspondent} onChange={(e) => set({ correspondent: e.target.checked })} mb={1}>
              Correspondent
            </Checkbox>
            {state.correspondent && (
              <FormControl mb={2}>
                <FormLabel fontSize="sm" mb={1}>Investor (margin row)</FormLabel>
                <CreatableSelect
                  size="sm"
                  options={lenderOptions}
                  value={state.investor ? { value: state.investor, label: state.investor } : null}
                  onChange={(o) => set({ investor: o ? o.value : '' })}
                  placeholder="Investor…"
                  isClearable
                />
              </FormControl>
            )}

            <FormControl mb={2}>
              <FormLabel fontSize="sm" mb={1}>Pricing</FormLabel>
              <ChakraSelect size="sm" value={state.pricing} onChange={(e) => set({ pricing: e.target.value })}>
                <option value="auto">Auto (from margin)</option>
                <option value="LP">Lender-Paid</option>
                <option value="BP">Borrower-Paid</option>
              </ChakraSelect>
            </FormControl>

            <Flex gap={2} mb={2}>
              <FormControl>
                <FormLabel fontSize="sm" mb={1}>Loan amount</FormLabel>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none" color="gray.400">$</InputLeftElement>
                  <NumberInput size="sm" w="100%" value={state.loanAmount} onChange={(v) => set({ loanAmount: v })}>
                    <NumberInputField pl={6} />
                  </NumberInput>
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" mb={1}>Actual revenue</FormLabel>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none" color="gray.400">$</InputLeftElement>
                  <NumberInput size="sm" w="100%" value={state.revenue} onChange={(v) => set({ revenue: v })}>
                    <NumberInputField pl={6} />
                  </NumberInput>
                </InputGroup>
              </FormControl>
            </Flex>

            <Flex gap={4} mb={2}>
              <Checkbox
                size="sm"
                isChecked={state.companyDeal}
                onChange={(e) => set({ companyDeal: e.target.checked, brokerRef: e.target.checked ? false : state.brokerRef })}
              >
                Company deal
              </Checkbox>
              <Checkbox
                size="sm"
                isChecked={state.brokerRef}
                onChange={(e) => set({ brokerRef: e.target.checked, companyDeal: e.target.checked ? false : state.companyDeal })}
              >
                Broker referral
              </Checkbox>
            </Flex>

            <FormControl mb={2}>
              <FormLabel fontSize="sm" mb={1}>Secondary LO (optional)</FormLabel>
              <Select
                size="sm"
                options={loOptions}
                value={loOptions.find((o) => o.value === state.secondaryLoEmail) || null}
                onChange={(o) => set({ secondaryLoEmail: o ? o.value : '' })}
                placeholder="None"
                isClearable
              />
            </FormControl>
            {state.secondaryLoEmail && (
              <FormControl mb={2}>
                <FormLabel fontSize="sm" mb={1}>Manual secondary amount</FormLabel>
                <NumberInput size="sm" value={state.manualSecondary} onChange={(v) => set({ manualSecondary: v })}>
                  <NumberInputField placeholder="blank = rules decide" />
                </NumberInput>
              </FormControl>
            )}

            <Divider my={2} />

            <Flex align="center" gap={2} mb={2}>
              <Checkbox
                size="sm"
                isChecked={state.feeMode === 'manual' ? state.feeCharged : autoFee !== null && autoFee !== 0}
                onChange={(e) => set({ feeMode: 'manual', feeCharged: e.target.checked, feeAmount: state.feeAmount === '' && autoFee ? autoFee : state.feeAmount })}
              >
                Processing fee
              </Checkbox>
              {state.feeMode === 'manual' ? (
                <>
                  {state.feeCharged && (
                    <NumberInput size="xs" maxW="100px" value={state.feeAmount} onChange={(v) => set({ feeAmount: v })}>
                      <NumberInputField />
                    </NumberInput>
                  )}
                  <Badge colorScheme="yellow">override</Badge>
                  <Button size="xs" variant="ghost" onClick={() => set({ feeMode: 'auto', feeAmount: '' })}>auto</Button>
                </>
              ) : (
                <Text fontSize="xs" color="gray.500">auto: {autoFee === null ? '—' : money(autoFee)}</Text>
              )}
            </Flex>

            <Flex gap={4} mb={2}>
              <Checkbox size="sm" isChecked={state.heloc} onChange={(e) => set({ heloc: e.target.checked })}>HELOC</Checkbox>
              <Checkbox size="sm" isChecked={state.processorAssigned} onChange={(e) => set({ processorAssigned: e.target.checked })}>
                Processor assigned
              </Checkbox>
            </Flex>

            <Flex gap={2} mb={2}>
              <FormControl>
                <FormLabel fontSize="sm" mb={1}>Submission adder</FormLabel>
                <ChakraSelect size="sm" value={state.adder} onChange={(e) => set({ adder: e.target.value })}>
                  <option value="auto">Auto (from comp code)</option>
                  <option value="none">None</option>
                  <option value="2nd">2nd submission</option>
                  <option value="3rd">3rd submission</option>
                </ChakraSelect>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" mb={1}>
                  Month CD count{' '}
                  {!state.monthCountOverridden && liveCounts && <Badge fontSize="0.6em" colorScheme="green">live</Badge>}
                </FormLabel>
                <NumberInput
                  size="sm"
                  min={1}
                  value={state.monthCount}
                  onChange={(v) => set({ monthCount: v === '' ? 1 : Number(v), monthCountOverridden: true })}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
            </Flex>

            <FormControl mb={2}>
              <FormLabel fontSize="sm" mb={1}>Closing date</FormLabel>
              <Input size="sm" type="date" value={state.closingDate}
                onChange={(e) => set({ closingDate: e.target.value, monthCountOverridden: false })} />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" mb={1}>Comp code (tag testbed)</FormLabel>
              <Input size="sm" value={state.compCode} onChange={(e) => set({ compCode: e.target.value })}
                placeholder='e.g. "CD", "SS", "BP", "2nd submission"…' />
            </FormControl>
          </PanelBox>
        </GridItem>

        {/* -------------------- RESOLVED FACTS (middle) -------------------- */}
        <GridItem>
          {prof && (
            <PanelBox title="LO profile">
              <Fact label="Name">{prof.name || prof.email}</Fact>
              <Fact label="Status">
                <Badge colorScheme={prof.status === 'Active' ? 'green' : prof.status === 'VERIFY' ? 'orange' : 'gray'}>
                  {prof.status || '—'}
                </Badge>
                {prof.presidentsClub ? <Badge ml={1} colorScheme="yellow">President's Club</Badge> : null}
              </Fact>
              <Fact label="W2 / plan">{isW2(prof) ? 'W2' : '1099'} · {prof.planType || '—'}{prof.branch ? ` · ${prof.branch}` : ''}</Fact>
              <Fact label="Self-sourced">LP {rateFmt(prof.ssLP)} · BP {rateFmt(prof.ssBP)} of rev · fee {money(prof.ssFee)}</Fact>
              <Fact label="Company deal">
                {prof.cdTier1
                  ? `LP tiers ${prof.cdTier1}% → ${rateFmt(prof.cdTier2)} @ ${prof.cdTrigger}/mo · BP ${rateFmt(prof.cdBP)} · fee ${money(prof.cdFee)}`
                  : prof.cdFlat
                    ? `flat ${money(prof.cdFlat)}${prof.cdFlatTier2 ? ` → ${money(prof.cdFlatTier2)} @ ${prof.cdTrigger}/mo` : ''}`
                    : '— (SS fallback)'}
              </Fact>
              <Fact label="Other">
                HELOC {rateFmt(prof.helocPct)} · broker-ref {money(prof.brokerRefFlat)} · BP floor {money(prof.bpFloor)} · MaxComp fallback {rateFmt(prof.maxCompFallback)}
              </Fact>
              <Fact label="Waivers">
                {prof.feeWaivedCorr ? 'correspondent ' : ''}{prof.feeWaivedHelocNoProc ? '· HELOC-no-processor' : ''}
                {!prof.feeWaivedCorr && !prof.feeWaivedHelocNoProc ? 'none' : ''}
              </Fact>
            </PanelBox>
          )}

          {res && (
            <PanelBox title="Lender / margin">
              <Fact label="Margin row">{resolved.marginLender || '—'}{resolved.isCorrespondent ? ' (via Investor)' : ''}</Fact>
              <Fact label="Column">{resolved.marginColumn || '—'}</Fact>
              <Fact label="Margin">
                {resolved.marginPct == null ? 'BP / no numeric margin' : `${resolved.marginPct}%`}
                {marginRow && marginRow.maxComp > 0 ? ` · Max Comp ${money(marginRow.maxComp)}` : ''}
              </Fact>
              <Fact label="Correspondent">
                <Badge colorScheme={resolved.isCorrespondent ? 'cyan' : 'gray'}>
                  {resolved.isCorrespondent ? 'yes' : 'no'}
                </Badge>
              </Fact>
            </PanelBox>
          )}

          {res && (
            <PanelBox title="Deal math">
              <Fact label="Expected revenue">
                {expectedRevenue === null ? '—' : `${money(expectedRevenue)} (loan × ${resolved.marginPct}%)`}
              </Fact>
              <Fact label="Actual revenue">{state.revenue === '' ? '—' : money(state.revenue)}</Fact>
              {expectedRevenue !== null && state.revenue !== '' && (
                <Fact label="Shortfall">
                  {money(expectedRevenue - Number(state.revenue))}
                  {snap.revived.config && snap.revived.config.bp_drift_threshold != null
                    ? ` (BP if > ${money(snap.revived.config.bp_drift_threshold)})`
                    : ''}
                </Fact>
              )}
              <Fact label="Pricing">
                <Badge colorScheme={resolved.pricing === 'BP' ? 'blue' : resolved.pricing === 'LP' ? 'green' : 'purple'}>
                  {resolved.pricing || '—'}
                </Badge>
                {resolved.bpReason ? <Text as="span" fontSize="xs" color="gray.500" ml={2}>{resolved.bpReason}</Text> : null}
              </Fact>
              <Fact label="Deal source">{resolved.dealSource || '—'}{resolved.dealSourceHow ? ` (${resolved.dealSourceHow})` : ''}</Fact>
              <Fact label="Rate set">{resolved.rateSet || '—'}{resolved.tierApplied ? ` · tier ${resolved.tierApplied}` : ''}</Fact>
              <Fact label="Split rule">{resolved.splitRuleId || 'none matched'}</Fact>
              {state.secondaryLoEmail && snap.revived.loaFees && snap.revived.loaFees[state.secondaryLoEmail] && (
                <Fact label="LOA schedule">
                  deduct {money(snap.revived.loaFees[state.secondaryLoEmail].deduct)} · pay {money(snap.revived.loaFees[state.secondaryLoEmail].pay)}
                </Fact>
              )}
            </PanelBox>
          )}

          {!prof && (
            <Alert status="info" borderRadius="md" fontSize="sm">
              <AlertIcon />Pick a loan officer to see resolved facts.
            </Alert>
          )}
        </GridItem>

        {/* ------------------------- OUTPUTS (right) ------------------------ */}
        <GridItem>
          {result && result.error && (
            <Alert status="error" borderRadius="md" mb={3}><AlertIcon />{result.error}</Alert>
          )}

          {hasOverride && (
            <Alert status="warning" borderRadius="md" mb={3} fontSize="sm" bg={overrideBg}>
              <AlertIcon />
              Manual overrides active ({[
                state.pricing !== 'auto' && `pricing ${state.pricing}`,
                state.feeMode === 'manual' && 'fee',
                state.adder !== 'auto' && `adder ${state.adder}`,
              ].filter(Boolean).join(', ')}) — batch runs never pass these.
            </Alert>
          )}

          <PanelBox title="Result">
            <Flex gap={6} wrap="wrap" mb={2}>
              <Stat><StatLabel>Primary</StatLabel><StatNumber>{res ? money(res.primary) : '—'}</StatNumber></Stat>
              <Stat><StatLabel>Secondary</StatLabel><StatNumber>{res && res.secondary !== null ? money(res.secondary) : '—'}</StatNumber></Stat>
            </Flex>
            {res && res.thirdParty && res.thirdParty.length > 0 && (
              <Box mb={2}>
                <Text fontSize="sm" fontWeight="semibold">Third-party payouts</Text>
                {res.thirdParty.map((tp, i) => (
                  <Text key={i} fontSize="sm">{tp.email} — {money(tp.amount)}{tp.note ? ` (${tp.note})` : ''}</Text>
                ))}
              </Box>
            )}
            <Text fontSize="sm" fontWeight="semibold" mb={1}>Flags</Text>
            <FlagTags flags={flags} />
            {res && (
              <>
                <Text fontSize="sm" fontWeight="semibold" mt={3} mb={1}>Formula (written to Comp Formula Used)</Text>
                <Code fontSize="xs" p={2} borderRadius="md" display="block" whiteSpace="pre-wrap">{res.formula}</Code>
                {res.compCodeAppend && (
                  <Text fontSize="xs" color="gray.500" mt={1}>Comp-code append: <Code fontSize="xs">{res.compCodeAppend}</Code></Text>
                )}
              </>
            )}
            {!res && result && !result.error && (
              <Alert status="warning" borderRadius="md" mt={2} fontSize="sm">
                <AlertIcon />Row skipped — see flags for why.
              </Alert>
            )}
          </PanelBox>

          {res && res.steps && (
            <PanelBox title="Steps (precedence order)">
              {res.steps.map((s) => (
                <Flex key={s.n} fontSize="sm" gap={2} py={0.5} color={s.applied ? undefined : stepSkipColor}>
                  <Text minW="20px" textAlign="right" color="gray.500">{s.n}.</Text>
                  <Box>
                    <Text as="span" fontWeight="semibold">{s.label}:</Text>{' '}
                    <Text as="span">{s.detail}</Text>
                    {!s.applied && <Badge ml={1} fontSize="0.6em" variant="outline">skipped</Badge>}
                  </Box>
                </Flex>
              ))}
            </PanelBox>
          )}
        </GridItem>
      </Grid>

      <Text fontSize="xs" color="gray.500" mt={4}>
        Reference snapshot <Code fontSize="xs">{snap.hash.slice(0, 12)}…</Code> loaded{' '}
        {snap.loadedAt.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} ET
        {' · '}engine <Code fontSize="xs">{ENGINE_VERSION}</Code>
        {' · '}<Button size="xs" variant="link" onClick={loadSnapshot}>refresh snapshot</Button>
      </Text>

      {/* ---------------- Load-from-pipeline-row modal (§9.5) --------------- */}
      <Modal isOpen={rowModalOpen} onClose={() => setRowModalOpen(false)} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Load from pipeline row</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex gap={2} mb={3}>
              <Input
                placeholder="Client name or exact loan number…"
                value={rowQuery}
                onChange={(e) => setRowQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') searchRows(); }}
                autoFocus
              />
              <Button onClick={searchRows} isLoading={rowSearching} colorScheme="blue">Search</Button>
            </Flex>
            {rowMatches && rowMatches.length === 0 && (
              <Text fontSize="sm" color="gray.500">No matching rows on the live tab.</Text>
            )}
            {rowMatches && rowMatches.length > 0 && renderRowTable(rowMatches)}
            {!rowMatches && (
              <>
                <Flex align="center" justify="space-between" mb={1}>
                  <Text fontSize="sm" fontWeight="semibold">Latest pipeline rows</Text>
                  {recentLoading && <Spinner size="xs" />}
                </Flex>
                {recentRows && recentRows.length > 0 && renderRowTable(recentRows)}
                {recentRows && recentRows.length === 0 && !recentLoading && (
                  <Text fontSize="sm" color="gray.500">Could not load the latest rows — use search above.</Text>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      {/* ------------------- Save-golden-case modal (§9.6) ------------------ */}
      <Modal isOpen={goldenOpen} onClose={() => setGoldenOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save as golden test case</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.500" mb={2}>
              Freezes the current inputs and result as a regression case. Export to the Jest fixture
              with <Code fontSize="xs">scripts/payroll/export-golden-cases.js</Code>.
            </Text>
            <FormControl>
              <FormLabel fontSize="sm">Case name</FormLabel>
              <Input value={goldenName} onChange={(e) => setGoldenName(e.target.value)}
                placeholder='e.g. "Hannah 80bps + correspondent waiver"' autoFocus />
            </FormControl>
            {res && (
              <Text fontSize="sm" mt={3}>
                Expected: primary <b>{money(res.primary)}</b>
                {res.secondary !== null ? <> · secondary <b>{money(res.secondary)}</b></> : null}
                {flags.length ? <> · {flags.length} flag(s)</> : null}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setGoldenOpen(false)}>Cancel</Button>
            <Button colorScheme="purple" onClick={saveGolden} isLoading={goldenSaving} isDisabled={!goldenName.trim()}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
